import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { of, catchError, finalize, tap, concatMap, Observable, Subscription, combineLatest, map, startWith } from 'rxjs';

import { FORBIDDEN_NAMES, INDUSTRIES } from '@shared/restaurant/constants';
import { UpdateAccountRequest } from '@shared/account/models';
import { AuthUser } from '@shared/signup/models';
import {
  ACCOUNT_LOCATION_NAME_PATTERN,
  forbiddenNamesValidator,
  urlValidator,
} from '@tablesready/common-ui';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AccountApiService, AuthLocationService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-organization-data',
  templateUrl: './organization-data.component.html',
  styleUrls: ['./organization-data.component.scss'],
})
export class OrganizationDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: AuthUser;

  industries = INDUSTRIES;
  form: UntypedFormGroup;
  submitting = false;
  size = AppIconSize;
  icon = AppIcon;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountApiService: AccountApiService,
    private readonly alertsService: AlertsService,
    private readonly authLocationService: AuthLocationService,
  ) { }

  ngOnInit(): void {
    const subscription = combineLatest([
      this.authLocationService.selectAll()
        .pipe(
          map(locations => locations
            .filter(location => location.accountId === this.user.account.id && location.integrations?.reserveWithGoogle?.enabled)
            .length > 0
          ),
        ),
      this.form.get('websiteUrl').valueChanges
        .pipe(
          startWith(this.form.get('websiteUrl').value),
        ),
    ])
      .pipe(
        tap(([hasEnabledGoogleReservation, value]) => {
          if (!value && hasEnabledGoogleReservation) {
            this.form.get('applyWebsiteToAll').disable();
          } else {
            this.form.get('applyWebsiteToAll').enable();
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(subscription);
  }

  ngOnChanges(): void {
    this.form = this.formBuilder.group({
      name: [this.user.account.name, [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(ACCOUNT_LOCATION_NAME_PATTERN),
        forbiddenNamesValidator(FORBIDDEN_NAMES),
      ]],
      industry: [this.user.account.industry, [Validators.required]],
      websiteUrl: [this.user.account.websiteUrl, urlValidator],
      applyWebsiteToAll: [false],
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submit(): void {
    this.submitting = true;
    const { name: orgName, industry, websiteUrl, applyWebsiteToAll } = this.form.value;
    const payload: UpdateAccountRequest = {
      name: orgName,
      industry,
      websiteUrl,
    };

    this.accountApiService.update(payload)
      .pipe(
        tap(() => this.alertsService.addSuccess('Organization info', 'Saved:')),
        catchError((error) => {
          this.alertsService.addError(`${error.error.error}`);

          return of(null);
        }),
        concatMap(() => {
          if (applyWebsiteToAll) {
            return this.resetWebsiteUrl();
          }

          return of(undefined);
        }),
        finalize(() => this.submitting = false),
      )
      .subscribe();
  }

  reset(): void {
    this.form.reset({
      name: this.user.account.name,
      industry: this.user.account.industry,
      websiteUrl: this.user.account.websiteUrl,
      applyWebsiteToAll: false,
    });
  }

  resetWebsiteUrl(): Observable<unknown> {
    return this.accountApiService.resetWebsiteUrl()
      .pipe(
        tap(() => this.alertsService.addSuccess('Website URL on all locations is updated.', '')),
        catchError(() => {
          this.alertsService.addError('resetting website url failed');

          return of(null);
        }),
      );
  }
}
